import { useEffect, useState } from 'react';
import { isApiConfigured, listCategories, listPosts, peekCached } from './api';
import { BLOGS } from '../data/mockContent';
import { Category, PostSummary } from '../types/content';

/**
 * The three original articles live as markdown files under public/content and
 * predate the database. They are folded into the index so nothing that was
 * already published disappears once the API goes live.
 */
export const LEGACY_POSTS: PostSummary[] = BLOGS.map((blog) => ({
  id: `legacy-${blog.id}`,
  slug: blog.id,
  title: blog.title,
  excerpt: blog.excerpt,
  coverImage: `${process.env.PUBLIC_URL || ''}/images/${blog.id}.svg`,
  category: 'system-design',
  tags: blog.tags,
  status: 'published',
  authorName: blog.author,
  readingMinutes: 8,
  publishedAt: blog.date,
  // These articles predate the review workflow and never went through it.
  submittedAt: null,
  reviewedBy: null,
  reviewedAt: null,
  reviewNote: null,
  createdAt: blog.date,
  updatedAt: blog.date,
}));

export const isLegacySlug = (slug: string) => BLOGS.some((b) => b.id === slug);

export interface BlogIndex {
  posts: PostSummary[];
  categories: Category[];
  loading: boolean;
  /** Set when the API could not be reached; the legacy posts are shown instead. */
  error: string | null;
}

function withCounts(categories: Category[], posts: PostSummary[]): Category[] {
  return categories.map((category) => ({
    ...category,
    postCount: posts.filter((p) => p.category === category.slug).length,
  }));
}

/** Categories the legacy posts need, in case the API is unreachable. */
const FALLBACK_CATEGORIES: Category[] = [
  {
    id: 'fallback-system-design',
    slug: 'system-design',
    name: 'System Design',
    description: 'Architecture deep dives, scalability patterns and distributed systems.',
    order: 1,
    createdAt: '',
    updatedAt: '',
  },
];

/**
 * Newest first, with the database posts and the original markdown articles
 * interleaved by publish date.
 */
function mergeWithLegacy(apiPosts: PostSummary[]): PostSummary[] {
  return [...apiPosts, ...LEGACY_POSTS].sort((a, b) => {
    const da = a.publishedAt ? Date.parse(a.publishedAt) : 0;
    const db = b.publishedAt ? Date.parse(b.publishedAt) : 0;
    return db - da;
  });
}

/** How stale a cached index may be and still be worth painting immediately. */
const SEED_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * The index as of the last time it was fetched, if that was recent.
 *
 * This is what removes the flash on reload: without it the first render has
 * nothing but an empty array, so the page shows "Loading articles…" and then
 * replaces it — the two-stage load that looked like one screen appearing over
 * another. A revalidating fetch still runs on mount, so a seeded render is
 * corrected within a few hundred milliseconds if anything has changed.
 */
function seedFromCache(): { posts: PostSummary[]; categories: Category[] } | null {
  if (!isApiConfigured) return null;

  const apiPosts = peekCached<PostSummary[]>('posts', SEED_MAX_AGE_MS);
  if (!apiPosts) return null;

  const apiCategories = peekCached<Category[]>('categories', SEED_MAX_AGE_MS);
  const merged = mergeWithLegacy(apiPosts);
  return {
    posts: merged,
    categories: withCounts(apiCategories?.length ? apiCategories : FALLBACK_CATEGORIES, merged),
  };
}

export function useBlogIndex(): BlogIndex {
  // Lazy initialisers: reading storage once on mount, not on every render.
  const [seed] = useState(seedFromCache);
  const [posts, setPosts] = useState<PostSummary[]>(() => seed?.posts ?? []);
  const [categories, setCategories] = useState<Category[]>(() => seed?.categories ?? []);
  const [loading, setLoading] = useState(!seed);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const applyFallback = (message: string | null) => {
      if (cancelled) return;
      setError(message);
      setLoading(false);
      // A failed revalidation must not replace a page that is already showing
      // the real index with the three-article archive. Keep what is on screen
      // and let the notice explain itself.
      if (seed) return;
      setPosts(LEGACY_POSTS);
      setCategories(withCounts(FALLBACK_CATEGORIES, LEGACY_POSTS));
    };

    if (!isApiConfigured) {
      applyFallback(null);
      return;
    }

    (async () => {
      try {
        const [apiPosts, apiCategories] = await Promise.all([
          listPosts(),
          listCategories().catch(() => [] as Category[]),
        ]);
        if (cancelled) return;

        const merged = mergeWithLegacy(apiPosts);

        // Every section is shown, including empty ones — they are the site's
        // structure, and an empty one renders its own "no articles yet" state.
        const known = apiCategories.length ? apiCategories : FALLBACK_CATEGORIES;
        setPosts(merged);
        setCategories(withCounts(known, merged));
        setError(null);
        setLoading(false);
      } catch (e) {
        applyFallback(e instanceof Error ? e.message : 'Could not reach the blog API.');
      }
    })();

    return () => {
      cancelled = true;
    };
    // `seed` is set once by a lazy initialiser and never changes, so this
    // still runs exactly once.
  }, [seed]);

  return { posts, categories, loading, error };
}
